'use client';

import { ArrowUpRight, ChevronDown } from 'lucide-react';
import dynamic from 'next/dynamic';
import Link from "next/link";
import { useState, useEffect, useRef } from 'react';
import CredentialsMarquee from '@/components/shared/CredentialsMarquee';
import { companyAge } from "@utils/companyAge";
const UniverseSphere = dynamic(() => import('@shared/UniverseSphere'), { ssr: false });

const SLIDES = [
  {
    badge: `${companyAge} Years · 500+ Clients · ISO 27001 Certified`,
    line1: 'Offshore',
    accent: 'Software',
    line3: ' Development Company',
    desc: 'Providing innovative software applications based on quality, scalability, and innovation',
  },
  {
    badge: 'iOS · Android · Cross-Platform · PWA',
    line1: 'Mobile App',
    accent: 'Development',
    line3: 'Experts',
    desc: 'Fluid, fast, and user-friendly mobile apps built for scale — delivering seamless experiences on every device',
  },
  {
    badge: 'AI · ML · NLP · Intelligent Automation',
    line1: 'AI & Machine',
    accent: 'Learning',
    line3: ' Solutions',
    desc: 'Custom-built AI models and automation tools that transform your business data into measurable outcomes',
  },
];

const INTERVAL = 4500;

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [tick, setTick] = useState(0); // increment to re-trigger animation
  const [isPaused, setIsPaused] = useState(false);
  const [showSphere, setShowSphere] = useState(false);
  const slideRef = useRef(null);
  const stripeRef = useRef(null);

  // Three.js sphere is desktop-only — don't download/boot it on mobile, and wait
  // until the browser is idle so it doesn't compete with the first paint.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    let idleId;
    const schedule = () => {
      if (!mq.matches) return;
      const ric = window.requestIdleCallback ?? (cb => setTimeout(cb, 1200));
      idleId = ric(() => setShowSphere(true));
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    const onChange = () => { if (mq.matches) setShowSphere(true); };
    mq.addEventListener('change', onChange);
    return () => {
      window.removeEventListener('load', schedule);
      mq.removeEventListener('change', onChange);
      if (idleId) (window.cancelIdleCallback ?? clearTimeout)(idleId);
    };
  }, []);

  // Re-animate on slide change without remounting the DOM nodes. Remounting
  // (key={tick}) created a brand-new LCP candidate every slide, pushing LCP out.
  // The first slide renders visible straight from the server HTML.
  useEffect(() => {
    if (tick === 0) return;
    slideRef.current?.animate(
      [{ opacity: 0, transform: 'translateY(28px)' }, { opacity: 1, transform: 'translateY(0)' }],
      { duration: 650, easing: 'ease', fill: 'both' }
    );
    stripeRef.current?.animate(
      [{ transform: 'scaleX(0)', opacity: 0 }, { transform: 'scaleX(1)', opacity: 0.65 }],
      { duration: 800, delay: 550, easing: 'ease-out', fill: 'both' }
    );
  }, [tick]);

  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => {
      setCurrent(c => (c + 1) % SLIDES.length);
      setTick(t => t + 1);
    }, INTERVAL);
    return () => clearInterval(id);
  }, [isPaused]);

  const slide = SLIDES[current];
  const HeadingTag = 'h1';

  return (
    <>
      <section className="relative h-[700px] sm:h-screen flex flex-col overflow-hidden bg-linear-to-br from-slate-950 via-blue-950 to-slate-900">
        <div className="flex-1 min-h-0 relative flex flex-col justify-center">
          <div className="absolute -top-20 -right-10 w-60 h-60 sm:w-96 sm:h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" />
          <div className="absolute bottom-10 left-10 w-48 h-48 sm:w-80 sm:h-80 bg-red-500/20 rounded-full blur-3xl animate-pulse [animation-delay:1s] pointer-events-none" />
          <div className="relative z-10 max-w-[1440px] mx-auto w-full px-6 py-8 sm:py-10 lg:py-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] gap-0 items-center">
            <div className="flex flex-col gap-5 sm:gap-6 lg:gap-7 lg:pr-14">
              <div
                ref={slideRef}
                className="flex flex-col gap-5 sm:gap-6 lg:gap-7"
              >
                <span className="self-start flex items-center gap-2 border border-white/20 rounded-full px-3 py-1 sm:px-4 sm:py-1.5 text-[10px] sm:text-xs font-medium tracking-widest uppercase bg-white/10 text-blue-300 backdrop-blur-sm">
                  {slide.badge}
                </span>

                <HeadingTag className="font-heading font-black text-3xl sm:text-4xl md:text-6xl xl:text-7xl text-white leading-tight md:leading-18">
                  {slide.line1}{' '}
                  <span className="inline-block relative mt-1 mb-3">
                    <em className="not-italic bg-linear-to-r from-red-400 via-red-500 to-rose-400 bg-clip-text text-transparent">
                      {slide.accent}
                    </em>
                    <span
                      ref={stripeRef}
                      className="absolute left-0 -bottom-2 h-2 w-full origin-left rounded-sm pointer-events-none"
                      style={{
                        background: 'linear-gradient(90deg, #E0274A, transparent)',
                        animation: 'stripe-expand 0.8s ease-out 0.55s both',
                      }}
                    />
                  </span>
                  {slide.line3}
                </HeadingTag>

                <p className="text-blue-200/80 text-lg max-w-md leading-relaxed">
                  {slide.desc}
                </p>

                <div className='w-fit'>
                  <Link
                    href="/contact-us"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    className="btn-glass group relative overflow-hidden flex items-center gap-2 px-6 py-3 bg-linear-to-r from-red-500 to-rose-500 text-white font-semibold rounded-xl cursor-pointer"
                  >
                    <span className="relative z-10 flex items-center gap-2 group-hover:text-secondary transition-colors duration-400">
                      Get Business Software Solutions
                      <ArrowUpRight
                        size={16}
                        className="transition-transform duration-200 group-hover:translate-x-1"
                      />
                    </span>
                  </Link>
                </div>
              </div>
            </div>

            <div
              className="hidden lg:block h-full min-h-105 w-px"
              aria-hidden="true"
              style={{
                background: 'linear-gradient(180deg, transparent 0%, rgba(129,140,248,0.3) 20%, rgba(129,140,248,0.7) 50%, rgba(129,140,248,0.3) 80%, transparent 100%)',
              }}
            />

            {/* Right — UniverseSphere */}
            <div className="hidden lg:flex items-center justify-center lg:justify-end lg:pl-14">
              <div className="relative w-full max-w-105 mx-auto">
                <div className="absolute top-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" />
                {showSphere ? (
                  <UniverseSphere
                    size={480}
                    isDark
                    logoSrc="/images/VelanApps-Icon.webp"
                  />
                ) : (
                  <div style={{ width: '100%', aspectRatio: '1 / 1', maxWidth: 480 }} aria-hidden="true" />
                )}
              </div>
            </div>
          </div>

          <div
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            className="absolute left-6 z-20 hidden lg:flex lg:flex-col lg:top-1/2 lg:-translate-y-1/2 lg:gap-5 xl:flex-row xl:top-auto xl:translate-y-0 xl:bottom-6 xl:gap-3 2xl:flex-col 2xl:top-1/2 2xl:-translate-y-1/2 2xl:gap-5 justify-center"
          >
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => { setCurrent(i); setTick(t => t + 1); }}
                className="flex items-center gap-2 group min-h-6"
                aria-label={`${String(i + 1).padStart(2, '0')} - Slide ${i + 1}`}
              >
                <span
                  className={`font-mono text-[10px] font-semibold tracking-widest transition-all duration-300 ${i === current ? 'text-white opacity-100' : 'text-white/30 opacity-60'
                    }`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span
                  className={`block h-px rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-white' : 'w-3 bg-white/30 group-hover:w-5 group-hover:bg-white/50'
                    }`}
                />
              </button>
            ))}
          </div>

          <div
            onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/50 animate-bounce cursor-pointer"
          >
            <span className="text-xs tracking-widest uppercase">Scroll</span>
            <ChevronDown size={16} />
          </div>
        </div>

        <div>
          <CredentialsMarquee className="bg-transparent" />
        </div>

      </section>
    </>
  );
}
